import { LitElement, html, css } from 'lit';

/**
 * AnnouncementRegion - Screen reader only announcement component
 * Provides ARIA live region for status messages without visual display
 */
export class AnnouncementRegion extends LitElement {
  static properties = {
    message: { type: String, state: true },
    type: { type: String, state: true }
  };

  constructor() {
    super();
    this.message = '';
    this.type = 'status';
    this._clearTimeout = null;
  }

  createRenderRoot() {
    return this; // No shadow DOM for screen reader compatibility
  }

  /**
   * Announces a message to screen readers
   * @param {string} message - The message to announce
   * @param {string} type - The type of announcement (error, success, warning, info, status)
   * @param {Object} options - Additional options
   */
  announce(message, type = 'status', options = {}) {
    const {
      duration = 5000,
      priority = type === 'error' || type === 'warning' ? 'assertive' : 'polite'
    } = options;

    if (this._clearTimeout) {
      clearTimeout(this._clearTimeout);
    }

    this.setAttribute('aria-live', priority);
    this.setAttribute('aria-atomic', 'true');
    this.setAttribute('aria-relevant', 'additions text');
    this.setAttribute('role', 'status');

    this.message = '';
    this.type = type;

    setTimeout(() => {
      this.message = message;
      this.type = type;
    }, 100);

    // Auto-clear message after duration
    this._clearTimeout = setTimeout(() => {
      this.clear();
    }, duration);
  }

  /**
   * Clears the current announcement
   */
  clear() {
    this.message = '';
    if (this._clearTimeout) {
      clearTimeout(this._clearTimeout);
      this._clearTimeout = null;
    }
  }

  render() {
    if (!this.message) {
      return html``;
    }

    const prefixes = {
      error: 'Error: ',
      success: 'Success: ',
      warning: 'Warning: ',
      info: 'Information: ',
      status: ''
    };

    return html`${prefixes[this.type]}${this.message}`;
  }
}

customElements.define('announcement-region', AnnouncementRegion);
